import React, { Fragment, useContext, useEffect, useState, useMemo } from 'react'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import styled, { ThemeContext } from 'styled-components'
import { lighten } from 'polished';
import cn from 'classnames'

import cloneDeep from 'lodash/cloneDeep'
import findIndex from 'lodash/findIndex'
import flowRight from 'lodash/flowRight'
import upperCase from 'lodash/upperCase'
import pick from 'lodash/pick'
import Button from 'shared/components/Button'
import Box from 'shared/components/Box'
import DefaultImage from 'assets/images/launchpad_logo.png'
import ScreeningForm from 'shared/components/Screening/components/ScreeningForm'
import CollapsableFormContainer from 'shared/components/Screening/components/CollapsableFormContainer'
import useQuery from 'shared/hooks/useQuery'
import { organizationSettingSelector } from 'shared/hocs/withAuth'
import { useSelector } from 'react-redux'
import {
  ScreeningFormType,
  ScreeningAnswerType,
  ScreeningQuestionType,
  ScreeningParticipantType,
  getDummyQuestions
} from 'shared/components/Screening/models/screening'

import {
  QuestionFields
} from 'shared/components/Screening/models/question'

type Props = WrappedProps<ScreeningFormType> & {
  isPreview?: boolean
}

function MultiUserFormDialog(props: Props) {
  const {
    formState,
    formHandlers,
    isProcessing,
    isPreview = false,
    onCancel,
    onContinue,
    cancelLabel = 'Cancel',
    continueLabel = 'Continue'
  } = props
  const { organization_information } = useSelector(organizationSettingSelector)
  const theme = useContext(ThemeContext)
  const [showShadow, setShadowState] = useState(false)
  const { onChange, onSetForm } = formHandlers
  const { fields } = formState

  const [{ loading, data: quetionFields }] = useQuery<QuestionFields[]>(
    { url: 'note/screening/questions' },
    { initialData: [], isBase: false},
  )

  const formatted = quetionFields
    ?.filter(question => ['RADIO', 'RICHTEXT'].includes(upperCase(question?.type)))
    ?.map(qf => pick(qf, [
      'options', 'label', 'id', 'type', 'section_text'
    ]))

  const questions = formatted
    ?.filter(question => upperCase(question?.type) === 'RADIO')

  useEffect(() => {
    const participants = fields?.participants?.map((participant: ScreeningParticipantType) => {
      return {...participant, question_count: questions?.length}
    })

    onSetForm({ ...fields, participants })
  }, [questions?.length])

  const hasSelected = useMemo(() => {
    return fields?.participants
      ?.some((participant: ScreeningParticipantType) => participant.isSelected)
  }, [fields?.participants])

  return (
    <MultiUserFormDialogBodyContainer>
      <div className="form-logo-container">
        <img src={DefaultImage} />
      </div>
      <div className="form-description">
        This form is for each individual attending {organization_information.name}. <br />
        All fields are mandatory
      </div>
      <div className="form-content" onScroll={handleScroll}>
        {fields?.participants?.map((participant: ScreeningParticipantType, index: number) => (
          <CollapsableFormContainer
            isPreview={isPreview}
            participant={participant}
            onToggle={setShadowState}
            onSelect={handleSelectParticipant(participant.id)}
            key={`screening-form-${participant.id}`}
          >
            <ScreeningForm
              questions={formatted}
              isPreview={isPreview}
              isProcessing={isProcessing}
              participant_id={participant.id}
              answers={participant.answers}
              onChange={handleChange(participant.id)}
            />
          </CollapsableFormContainer>
        ))}
      </div>
      <div className={cn('md-dialog-footer md-dialog-footer--inline dialog__footer', {scroll: showShadow})}>
        {!hasSelected && (
          <StyledButton
            variant="outline-primary"
            onClick={onCancel}
            px="32px"
            mr="20px"
          >
            Back
          </StyledButton>
        )}
        {hasSelected && (
          <Fragment>
            <StyledButton
              variant="outline-primary"
              onClick={onCancel}
              px="32px"
              mr="20px"
            >
              {cancelLabel}
            </StyledButton>
            <StyledButton
              variant="primary"
              onClick={handleContinue}
              disabled={isNotYetCompleted()}
              px="32px"
            >
              {continueLabel}
            </StyledButton>
          </Fragment>
        )}
      </div>
    </MultiUserFormDialogBodyContainer>
  )

  function handleContinue() {
    if (!isPreview && isNotYetCompleted())
      return
    onContinue()
  }

  function isNotYetCompleted() {
    return fields?.participants?.some((participant: ScreeningParticipantType) => {
      return participant?.isSelected && participant?.question_count !== participant?.answers?.length
    })
  }

  function handleScroll(e: any) {
    const target = e.target
    const shouldHide = target.scrollHeight <= target.clientHeight

    if (!showShadow && !shouldHide)
      setShadowState(true)

    if (showShadow && shouldHide)
      setShadowState(false)
  }

  function handleSelectParticipant(participant_id: string) {
    return (isSelected: boolean) => {
      setShadowState(isSelected)

      const participants = cloneDeep(fields?.participants || [])
      const index = findIndex(participants, { id: participant_id })

      participants.splice(index, 1, { ...participants[index], isSelected })
      onChange('participants', participants)
    }
  }

  function handleChange(participant_id: string) {
    return (answers: ScreeningAnswerType[]) => {
      const participants = cloneDeep(fields?.participants || [])
      const index = findIndex(participants, { id: participant_id })

      participants.splice(index, 1, { ...participants[index], answers })
      onChange('participants', participants)
    }
  }
}

const WrappedMultiUserFormDialog = flowRight(withDialog())(MultiUserFormDialog)

const StyledButton = styled(Button)`
  height: 56px;
`

const MultiUserFormDialogBodyContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  padding: 40px 40px 0;
  width: 100%;
  max-width: 700px;

  .md-dialog-footer {
    padding: 16px !important;
    position: fixed;
    width: 100%;
    left: 0;
    bottom: 0;

    &.scroll {
      box-shadow: 0px -4px 8px rgb(0 0 0 / 20%);
    }
  }

  .form-logo-container {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;

    img {
      -webkit-user-drag: none;
      -khtml-user-drag: none;
      -moz-user-drag: none;
      -o-user-drag: none;
      user-drag: none;

      width: 100%;
      max-width: 110px;
      max-height: 88px;
    }
  }

  .form-description {
    width: 100%;
    max-width: 600px;
    margin-bottom: 40px;

    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};
    text-align: center;
  }

  .form-content {
    max-height: 500px;
    min-height: 150px;
    overflow: hidden;
    overflow-y: auto;
    margin-right: -32px;
    margin-bottom: 90px;

    & > * {
      margin-bottom: 40px;
    }

    ::-webkit-scrollbar-thumb {
      background-color: #919191;
      border-radius: 50px;
    }

    ::-webkit-scrollbar {
      width: 12px;
      height: 8px;
      background-color: #F5F5F5;
      border-radius: 5px;
    }
  }
`

WrappedMultiUserFormDialog.defaultProps = {
  continueIsDisabled(data: ScreeningFormType) {
    return data?.participants?.some((participant: ScreeningParticipantType) => {
      return participant?.question_count !== participant?.answers?.length
    })
  },
}

export default WrappedMultiUserFormDialog
